import type { TaxonomyTagNode } from '@/services/mp/taxonomy';

export interface TaxonomyOption {
  label: string;
  value: string;
}

export interface AreaCascaderOption {
  label: string;
  value: string;
  children?: AreaCascaderOption[];
}

export type AreaUuidPathMap = Record<string, string[]>;

export interface AreaCascaderData {
  options: AreaCascaderOption[];
  uuidPathMap: AreaUuidPathMap;
}

const normalizeText = (value: unknown) => String(value ?? '').trim();

/**
 * 将 Taxonomy 树转换为扁平的下拉选项
 * label 采用层级路径（如：中国 / 上海 / 浦东新区）
 */
export function flattenTaxonomyToOptions(
  nodes: TaxonomyTagNode[],
  parentLabel: string = '',
): TaxonomyOption[] {
  let options: TaxonomyOption[] = [];

  if (!nodes || !Array.isArray(nodes)) {
    return options;
  }

  for (const node of nodes) {
    const nodeUuid = normalizeText(node?.uuid);
    if (!nodeUuid) {
      continue;
    }
    const currentLabel = parentLabel
      ? `${parentLabel} / ${node.name}`
      : node.name;

    // 添加当前节点
    options.push({
      label: currentLabel,
      value: nodeUuid,
    });

    // 递归处理子节点
    if (node.children && node.children.length > 0) {
      options = options.concat(
        flattenTaxonomyToOptions(node.children, currentLabel),
      );
    }
  }

  return options;
}

const toAreaCascaderOptions = (
  nodes: TaxonomyTagNode[],
  parentPath: string[],
  uuidPathMap: AreaUuidPathMap,
): AreaCascaderOption[] => {
  if (!Array.isArray(nodes)) {
    return [];
  }

  const result: AreaCascaderOption[] = [];

  for (const node of nodes) {
    const value = normalizeText(node?.uuid);
    if (!value) {
      continue;
    }

    const label = normalizeText(node?.name) || value;
    const currentPath = [...parentPath, value];
    uuidPathMap[value] = currentPath;

    const children = toAreaCascaderOptions(
      node.children || [],
      currentPath,
      uuidPathMap,
    );

    result.push(
      children.length > 0
        ? {
            label,
            value,
            children,
          }
        : {
            label,
            value,
          },
    );
  }

  return result;
};

export function buildAreaCascaderData(
  nodes: TaxonomyTagNode[],
): AreaCascaderData {
  const uuidPathMap: AreaUuidPathMap = {};
  const options = toAreaCascaderOptions(nodes || [], [], uuidPathMap);
  return { options, uuidPathMap };
}

export function ensureAreaCascaderFallback(
  data: AreaCascaderData,
  areaUuid?: string,
  areaLabel?: string,
): AreaCascaderData {
  const normalizedUuid = normalizeText(areaUuid);
  if (!normalizedUuid) {
    return data;
  }

  if (data.uuidPathMap[normalizedUuid]) {
    return data;
  }

  const fallbackLabel = normalizeText(areaLabel) || normalizedUuid;

  return {
    options: [
      {
        value: normalizedUuid,
        label: fallbackLabel,
      },
      ...data.options,
    ],
    uuidPathMap: {
      ...data.uuidPathMap,
      [normalizedUuid]: [normalizedUuid],
    },
  };
}
